import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SEO from './SEO';
import Header from './Header';
import Footer from './Footer';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, User, Tag, ChevronRight, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const formatDate = (d) => {
  if (!d) return '';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
};

const readingTime = (html) => {
  if (!html) return '1 min';
  const text = html.replace(/<[^>]+>/g, '');
  const words = text.split(/\s+/).length;
  return `${Math.max(1, Math.round(words / 200))} min read`;
};

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/blog/posts/${slug}`);
        if (res.ok) setPost(await res.json());
        else setPost(null);
      } catch { setPost(null); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen bg-white"><Header />
      <div className="flex justify-center py-32"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900" /></div>
    <Footer /></div>
  );

  if (!post) return (
    <div className="min-h-screen bg-white"><Header />
      <div className="max-w-3xl mx-auto py-32 text-center">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Article Not Found</h1>
        <p className="text-gray-500 mb-6">This article may have been moved or removed.</p>
        <Button onClick={() => navigate('/research-learn')} className="bg-blue-900 hover:bg-blue-800">Back to Articles</Button>
      </div>
    <Footer /></div>
  );

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={post.meta_title || post.title} description={post.meta_description || post.excerpt} path={`/research-learn/${post.slug}`} />

      {/* Hero image */}
      {post.featured_image && (
        <div className="w-full h-64 sm:h-80 lg:h-[420px] overflow-hidden relative">
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
            <div className="max-w-3xl mx-auto">
              <Badge className="bg-white/90 text-slate-800 text-xs mb-3">{post.category}</Badge>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white leading-tight drop-shadow-lg">{post.title}</h1>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="bg-gray-50 border-b">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-2 text-xs text-gray-500">
          <button onClick={() => navigate('/research-learn')} className="hover:text-blue-700 flex items-center gap-1"><ArrowLeft size={12} /> Research & Learn</button>
          <ChevronRight size={12} />
          <span className="text-gray-700 truncate">{post.title}</span>
        </div>
      </div>

      {/* Article */}
      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
        {/* Title (only if no hero image) */}
        {!post.featured_image && (
          <div className="mb-10">
            <Badge className="bg-blue-100 text-blue-800 text-xs mb-3">{post.category}</Badge>
            <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-4">{post.title}</h1>
          </div>
        )}

        {/* Meta bar */}
        <div className="flex items-center gap-4 mb-10 pb-6 border-b border-gray-200">
          <span className="text-sm text-gray-500 flex items-center gap-1.5"><Clock size={14} className="text-gray-400" />{formatDate(post.published_at)}</span>
          <span className="text-sm text-gray-500 flex items-center gap-1.5"><User size={14} className="text-gray-400" />{post.author}</span>
          <span className="text-sm text-blue-600 font-medium ml-auto">{readingTime(post.content)}</span>
        </div>

        {/* Excerpt / intro */}
        {post.excerpt && (
          <p className="text-lg sm:text-xl text-gray-600 leading-relaxed mb-10 font-medium border-l-4 border-blue-900 pl-6">{post.excerpt}</p>
        )}

        {/* Content — styled via CSS class */}
        <div className="blog-content" dangerouslySetInnerHTML={{ __html: post.content }} data-testid="blog-post-content" />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="mt-14 pt-6 border-t border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <Tag size={14} className="text-gray-400" />
              {post.tags.map(t => (
                <span key={t} className="text-xs px-3 py-1.5 bg-gray-100 text-gray-600 rounded-full">{t}</span>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-14 bg-gradient-to-r from-slate-900 to-blue-900 rounded-2xl p-8 sm:p-10 text-center">
          <h3 className="text-xl font-bold text-white mb-2">Need Expert Help?</h3>
          <p className="text-blue-100/80 mb-6 max-w-md mx-auto">BCS provides nationwide general contracting services for capital improvements, structural rehabilitation, and asset protection.</p>
          <div className="flex gap-3 justify-center flex-wrap">
            <Button onClick={() => window.location.href = 'mailto:info@bcs-us.com?subject=Contact BCS&body=Hi BCS team,%0A%0AI%27d like to get in touch.%0A%0AThanks%21'} className="bg-white text-blue-900 hover:bg-blue-50 px-6">Contact Us</Button>
            <Button onClick={() => navigate('/research-learn')} variant="outline" className="border-white/30 text-white hover:bg-white/10 px-6">More Articles</Button>
          </div>
        </div>
      </article>

      {/* Blog content styles */}
      <style>{`
        .blog-content {
          color: #374151;
          font-size: 1.0625rem;
          line-height: 1.9;
        }
        .blog-content h2 {
          color: #0f172a;
          font-size: 1.625rem;
          font-weight: 700;
          margin-top: 3rem;
          margin-bottom: 1rem;
          padding-bottom: 0.5rem;
          border-bottom: 2px solid #e5e7eb;
          line-height: 1.3;
        }
        .blog-content h3 {
          color: #1e293b;
          font-size: 1.25rem;
          font-weight: 600;
          margin-top: 2rem;
          margin-bottom: 0.75rem;
          line-height: 1.4;
        }
        .blog-content p {
          margin-bottom: 1.5rem;
          line-height: 1.9;
        }
        .blog-content ul, .blog-content ol {
          margin: 1.25rem 0 1.75rem 0;
          padding-left: 1.5rem;
        }
        .blog-content ul {
          list-style-type: disc;
        }
        .blog-content ol {
          list-style-type: decimal;
        }
        .blog-content li {
          margin-bottom: 0.75rem;
          line-height: 1.75;
          padding-left: 0.25rem;
        }
        .blog-content li::marker {
          color: #1e3a5f;
        }
        .blog-content strong {
          color: #1e293b;
          font-weight: 600;
        }
        .blog-content a {
          color: #1d4ed8;
          font-weight: 500;
          text-decoration: none;
        }
        .blog-content a:hover {
          text-decoration: underline;
        }
        .blog-content blockquote {
          border-left: 4px solid #1e3a5f;
          padding: 1rem 1.5rem;
          margin: 1.5rem 0;
          background: #f8fafc;
          border-radius: 0 0.5rem 0.5rem 0;
          font-style: italic;
          color: #475569;
        }
      `}</style>

      <Footer />
    </div>
  );
};

export default BlogPost;
